import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CoachPanel } from "./CoachPanel";
import { SteeringWheel } from "./SteeringWheel";
import { TrackMap } from "./TrackMap";
import { TraceCharts, type ChartSpec, type Range, type Zone } from "./TraceCharts";
import { useChannelLabel } from "../i18n/channels";
import { coach } from "../lib/coach";
import { detectCorners, type Corner } from "../lib/corners";
import { formatChannelValue, formatSignedSeconds } from "../lib/format";
import {
  deltaToReference,
  type AlignedLap,
  type AlignedTrace,
  type TrackColourMode,
} from "../lib/trace";
import type { ChannelInfo } from "../lib/types";

/** Matches MAX_LAPS_PER_REQUEST on the API. */
export const MAX_COMPARED_LAPS = 8;
/** Matches MAX_CHANNELS_PER_REQUEST on the API. */
const MAX_REQUESTED_CHANNELS = 20;

/**
 * Fetched whether or not they are charted: the track map needs the GPS, where each
 * car sat across the road and where its edges were, and the colouring channels;
 * corner detection needs lateral G and brake, and the input widget needs steering
 * and gear.
 */
const REQUIRED_CHANNELS = [
  "gps_lat",
  "gps_lon",
  "lateral_offset",
  "track_edge",
  "throttle",
  "brake",
  "g_lat",
  "g_long",
  "steering",
  "gear",
  "speed",
];

const MAX_STACKED_CHANNELS = MAX_REQUESTED_CHANNELS - REQUIRED_CHANNELS.length;

/**
 * Gear is in here with the pedals because it is half of what the pedals mean: the
 * same throttle trace reads differently a gear higher, and a shift in the middle of
 * a corner is the sort of thing you only notice when it is on screen next to it.
 */
const DEFAULT_STACK = ["speed", "throttle", "brake", "gear"];

/** Offered as one-click chips; everything else is in the dropdown. */
const SUGGESTED_CHANNELS = [
  "speed",
  "throttle",
  "brake",
  "steering",
  "gear",
  "rpm",
  "g_lat",
  "g_long",
];

/**
 * Channels whose meaning depends on seeing the whole range rather than just the part
 * that was used. A throttle trace that autoscales to 60..100 reads as though the driver
 * never lifted.
 */
const FIXED_RANGES: Record<string, [number, number]> = {
  throttle: [0, 100],
  brake: [0, 100],
  clutch: [0, 100],
  steering: [-100, 100],
};

const COLOUR_MODES: TrackColourMode[] = ["pedals", "accel", "laps"];

/**
 * Which channels are charted, and therefore which have to be fetched.
 *
 * Owned by the page rather than by the workspace because it is an input to the
 * query, and the workspace only ever sees the answer.
 */
export function useChannelStack() {
  const [stack, setStack] = useState<string[]>(DEFAULT_STACK);

  const requestedChannels = useMemo(
    () =>
      Array.from(new Set([...REQUIRED_CHANNELS, ...stack])).slice(
        0,
        MAX_REQUESTED_CHANNELS,
      ),
    [stack],
  );

  function toggleChannel(name: string) {
    setStack((current) => {
      if (current.includes(name)) {
        return current.filter((value) => value !== name);
      }
      if (current.length >= MAX_STACKED_CHANNELS) {
        return current;
      }
      return [...current, name];
    });
  }

  return { stack, toggleChannel, requestedChannels };
}

interface Props {
  aligned: AlignedTrace;
  distanceStep: number;
  /** Everything the mapping offers, for the channel picker. */
  channels: ChannelInfo[];
  stack: string[];
  onToggleChannel: (name: string) => void;
  /** The lap everything is compared against; usually the quickest on screen. */
  reference: AlignedLap | null;
  /** Resets the zoom when it changes, e.g. on a new lap selection. */
  selectionKey: string;
  /** Goes under the track map: whatever picked these laps in the first place. */
  children?: ReactNode;
}

/**
 * The analysis view: a stack of charts, the inputs, the track map and the corner
 * list, all sharing one cursor and one zoom.
 *
 * It does not know where its laps came from. That is what lets the same view serve
 * a single session and a comparison between two drivers in a team, which are the
 * same question asked of different sets of laps.
 */
export function TraceWorkspace({
  aligned,
  distanceStep,
  channels,
  stack,
  onToggleChannel,
  reference,
  selectionKey,
  children,
}: Props) {
  const { t } = useTranslation();
  const channelLabel = useChannelLabel();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [colourMode, setColourMode] = useState<TrackColourMode>("pedals");
  const [range, setRange] = useState<Range | null>(null);
  const [studentKey, setStudentKey] = useState<string | null>(null);
  const [activeZone, setActiveZone] = useState<string | null>(null);

  const corners = useMemo(
    () => (reference ? detectCorners(reference, distanceStep) : []),
    [reference, distanceStep],
  );

  // A zoom is stored in metres along the shared axis, and that axis starts wherever
  // the earliest selected lap starts, so changing the selection can move it out from
  // under the zoom.
  useEffect(() => {
    setRange(null);
    setStudentKey(null);
  }, [selectionKey]);

  /** The lap being coached: whichever one is not the benchmark. */
  const student = useMemo(() => {
    if (!reference) return null;
    const others = aligned.laps.filter((lap) => lap.key !== reference.key);
    if (others.length === 0) return null;
    return others.find((lap) => lap.key === studentKey) ?? others[0];
  }, [aligned.laps, reference, studentKey]);

  const report = useMemo(
    () =>
      reference && student
        ? coach(aligned, corners, student, reference, distanceStep)
        : null,
    [aligned, corners, student, reference, distanceStep],
  );

  const zones = useMemo<Zone[]>(
    () =>
      (report?.zones ?? []).map((zone, index) => ({
        key: zone.key,
        fromM: zone.fromM,
        toM: zone.toM,
        badge: String(index + 1),
        active: zone.key === activeZone,
      })),
    [report, activeZone],
  );

  const focus = useMemo(() => {
    if (!range || aligned.distance.length === 0) return null;
    const step = distanceStep || 1;
    const base = aligned.distance[0];
    return {
      startIndex: Math.floor((range.min - base) / step),
      endIndex: Math.ceil((range.max - base) / step),
    };
  }, [aligned, range, distanceStep]);

  const activeCorner = useMemo(() => {
    if (!focus) return null;
    return (
      corners.find(
        (corner) =>
          corner.apexIndex >= focus.startIndex && corner.apexIndex <= focus.endIndex,
      ) ?? null
    );
  }, [corners, focus]);

  // Depended on as a string rather than through `t`, so that the specs below survive
  // a render they have no reason to change on. Rebuilding them rebuilds the plots.
  const deltaLabel = t("channels.delta", { lap: reference?.label ?? "" });

  const charts = useMemo<ChartSpec[]>(() => {
    if (aligned.laps.length === 0) return [];
    const specs: ChartSpec[] = [];

    if (reference && aligned.laps.length > 1) {
      specs.push({
        key: "delta",
        label: deltaLabel,
        unit: "s",
        zeroLine: true,
        height: 130,
        format: (value) => formatSignedSeconds(value),
        series: aligned.laps
          .filter((lap) => lap.key !== reference.key)
          .map((lap) => ({
            label: lap.label,
            colour: lap.colour,
            values: deltaToReference(lap, reference),
          })),
      });
    }

    for (const name of stack) {
      const info = aligned.meta[name];
      if (!info) continue;
      // A channel that holds its value between samples is a staircase, not a slope,
      // and drawing it as one would invent gears it was never in.
      const stepped = info.interpolate === "hold";
      specs.push({
        key: name,
        label: channelLabel(name, info.label),
        unit: info.unit,
        range: FIXED_RANGES[name],
        stepped,
        height: stepped ? 110 : undefined,
        format: (value) => formatChannelValue(value, info.scale),
        series: aligned.laps.map((lap) => ({
          label: lap.label,
          colour: lap.colour,
          values: lap.channels[name] ?? [],
        })),
      });
    }

    return specs;
  }, [aligned, stack, reference, deltaLabel, channelLabel]);

  const extraChannels = useMemo(
    () =>
      channels
        .filter((channel) => !SUGGESTED_CHANNELS.includes(channel.name))
        .map((channel) => ({
          ...channel,
          text: channelLabel(channel.name, channel.label),
        }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [channels, channelLabel],
  );

  const available = new Set(channels.map((channel) => channel.name));

  function focusCorner(corner: Corner) {
    const base = aligned.distance[0];
    const step = distanceStep || 1;
    setRange({
      min: base + corner.focusStartIndex * step,
      max: base + corner.endIndex * step,
    });
  }

  /** Zooming to a coached zone leaves a little air either side of it. */
  function focusZone(fromM: number, toM: number) {
    const margin = Math.max(20, (toM - fromM) * 0.1);
    setRange({ min: fromM - margin, max: toM + margin });
  }

  return (
    <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_26rem]">
      <section className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {SUGGESTED_CHANNELS.filter((name) => available.has(name)).map((name) => {
            const info = channels.find((candidate) => candidate.name === name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => onToggleChannel(name)}
                className={[
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  stack.includes(name)
                    ? "border-sky-500/60 bg-sky-500/10 text-sky-300"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200",
                ].join(" ")}
              >
                {channelLabel(name, info?.label)}
              </button>
            );
          })}

          <select
            value=""
            onChange={(event) => {
              if (event.target.value) onToggleChannel(event.target.value);
            }}
            className="rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1 text-xs text-zinc-400"
          >
            <option value="">{t("workspace.moreChannels")}</option>
            {extraChannels.map((channel) => (
              <option key={channel.name} value={channel.name}>
                {stack.includes(channel.name) ? "✓ " : ""}
                {channel.text}
              </option>
            ))}
          </select>
        </div>

        {range && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <span className="text-zinc-300">
              {activeCorner
                ? t("workspace.turn", { number: activeCorner.number })
                : t("workspace.range", {
                    from: Math.round(range.min),
                    to: Math.round(range.max),
                  })}
            </span>
            <span className="text-xs text-zinc-500">
              {t("workspace.window", { metres: Math.round(range.max - range.min) })}
            </span>
            <button
              type="button"
              onClick={() => setRange(null)}
              className="rounded-md border border-zinc-700 px-2 py-0.5 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
            >
              {t("workspace.fullLap")}
            </button>
          </div>
        )}

        {report && (
          <CoachPanel
            report={report}
            laps={aligned.laps}
            onPickStudent={setStudentKey}
            onHoverZone={setActiveZone}
            onFocusZone={focusZone}
          />
        )}

        {charts.length > 0 && (
          <Readout
            aligned={aligned}
            charts={charts}
            reference={reference}
            hoverIndex={hoverIndex}
          />
        )}

        <div
          className="mt-4 rounded-xl border border-zinc-800"
          onMouseLeave={() => setHoverIndex(null)}
        >
          {charts.length === 0 ? (
            <p className="py-16 text-center text-sm text-zinc-500">
              {t("workspace.pickChannel")}
            </p>
          ) : (
            <TraceCharts
              distance={aligned.distance}
              charts={charts}
              range={range}
              onRangeChange={setRange}
              onHoverIndex={setHoverIndex}
              zones={zones}
            />
          )}
        </div>

        {charts.length > 0 && <Inputs aligned={aligned} hoverIndex={hoverIndex} />}
      </section>

      <aside className="min-w-0 space-y-8">
        <div>
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
              {t("workspace.track")}
              {reference && colourMode !== "laps" && (
                <span className="ml-2 normal-case tracking-normal text-zinc-600">
                  {reference.label}
                </span>
              )}
            </h2>
            <div className="flex rounded-md border border-zinc-700 p-0.5 text-xs">
              {COLOUR_MODES.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setColourMode(value)}
                  className={[
                    "rounded px-2 py-0.5 transition-colors",
                    colourMode === value
                      ? "bg-zinc-700 text-zinc-100"
                      : "text-zinc-400 hover:text-zinc-200",
                  ].join(" ")}
                >
                  {t(`workspace.colourModes.${value}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-zinc-800 p-4">
            {reference ? (
              <TrackMap
                laps={aligned.laps}
                reference={reference}
                mode={colourMode}
                hoverIndex={hoverIndex}
                focus={focus}
                corners={corners}
                distanceStep={distanceStep}
              />
            ) : (
              <p className="py-12 text-center text-sm text-zinc-500">
                {t("workspace.selectLap")}
              </p>
            )}
          </div>
        </div>

        {corners.length > 0 && (
          <div>
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
                {t("workspace.corners")}
              </h2>
              <span className="text-xs text-zinc-600">{t("workspace.cornersHint")}</span>
            </div>
            <div className="mt-4 max-h-72 overflow-y-auto rounded-xl border border-zinc-800">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-zinc-800">
                  {corners.map((corner) => (
                    <tr
                      key={corner.number}
                      onClick={() => focusCorner(corner)}
                      className={[
                        "cursor-pointer transition-colors",
                        activeCorner?.number === corner.number
                          ? "bg-sky-500/10"
                          : "hover:bg-zinc-900/40",
                      ].join(" ")}
                    >
                      <td className="px-3 py-2 tabular-nums text-zinc-200">
                        {t("workspace.turnShort")}
                        {corner.number}
                      </td>
                      <td className="px-3 py-2 text-zinc-400">
                        {t(`workspace.dirShort.${corner.direction}`)}{" "}
                        <span className="tabular-nums text-zinc-500">
                          {corner.turnedDegrees.toFixed(0)}°
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                        {corner.peakLateralG.toFixed(2)} G
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">
                        {corner.minSpeedKph === null
                          ? "—"
                          : `${corner.minSpeedKph.toFixed(0)} km/h`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {children}
      </aside>
    </div>
  );
}

interface InputsProps {
  aligned: AlignedTrace;
  hoverIndex: number | null;
}

function Inputs({ aligned, hoverIndex }: InputsProps) {
  const at = (lap: AlignedLap, name: string) =>
    hoverIndex === null ? null : (lap.channels[name]?.[hoverIndex] ?? null);

  return (
    <div className="mt-4 rounded-xl border border-zinc-800 px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
        {aligned.laps.map((lap) => (
          <SteeringWheel
            key={lap.key}
            label={lap.label}
            colour={lap.colour}
            steering={at(lap, "steering")}
            throttle={at(lap, "throttle")}
            brake={at(lap, "brake")}
            gear={at(lap, "gear")}
            speedKph={at(lap, "speed")}
          />
        ))}
      </div>
    </div>
  );
}

interface ReadoutProps {
  aligned: AlignedTrace;
  charts: ChartSpec[];
  reference: AlignedLap | null;
  hoverIndex: number | null;
}

/**
 * Exact values at the hovered point, above the charts rather than below them, since
 * it is read far more often than the page is scrolled.
 *
 * The charts carry no legends of their own: one table beats repeating the same lap
 * colours five times down the page, and the labels on the charts themselves give
 * the approximate value in place.
 */
function Readout({ aligned, charts, reference, hoverIndex }: ReadoutProps) {
  const { t } = useTranslation();
  const channelLabel = useChannelLabel();
  const channelKeys = charts.filter((chart) => chart.key !== "delta").map((c) => c.key);
  const distance = hoverIndex === null ? null : aligned.distance[hoverIndex];

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-zinc-800">
      <div className="flex items-baseline justify-between bg-zinc-900/60 px-3 py-2">
        <span className="text-xs uppercase tracking-wider text-zinc-500">
          {t("workspace.atCursor")}
        </span>
        <span className="tabular-nums text-sm text-zinc-300">
          {distance === null
            ? t("workspace.hoverChart")
            : t("workspace.metres", { metres: Math.round(distance) })}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr className="border-b border-zinc-800">
              <th className="px-3 py-2 font-medium">{t("workspace.columns.lap")}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t("workspace.columns.time")}
              </th>
              {reference && (
                <th className="px-3 py-2 text-right font-medium">
                  {t("workspace.columns.delta")}
                </th>
              )}
              {channelKeys.map((key) => (
                <th key={key} className="px-3 py-2 text-right font-medium">
                  {channelLabel(key, aligned.meta[key]?.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {aligned.laps.map((lap) => {
              const time = hoverIndex === null ? null : lap.time[hoverIndex];
              const referenceTime =
                hoverIndex === null || !reference ? null : reference.time[hoverIndex];
              const delta =
                time === null || referenceTime === null ? null : time - referenceTime;

              return (
                <tr key={lap.key}>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: lap.colour }}
                      />
                      <span className="whitespace-nowrap text-zinc-300">{lap.label}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-300">
                    {time === null ? "—" : time.toFixed(3)}
                  </td>
                  {reference && (
                    <td
                      className={[
                        "px-3 py-2 text-right tabular-nums",
                        delta === null
                          ? "text-zinc-500"
                          : delta > 0
                            ? "text-red-400"
                            : "text-emerald-400",
                      ].join(" ")}
                    >
                      {lap.key === reference.key
                        ? t("compare.reference")
                        : formatSignedSeconds(delta)}
                    </td>
                  )}
                  {channelKeys.map((key) => (
                    <td
                      key={key}
                      className="px-3 py-2 text-right tabular-nums text-zinc-400"
                    >
                      {formatChannelValue(
                        hoverIndex === null
                          ? null
                          : (lap.channels[key]?.[hoverIndex] ?? null),
                        aligned.meta[key]?.scale ?? 1,
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
